import { supabase } from './supabase';

/**
 * ⭐ Writes to a per-day log row (one row per user per day) that can't lose another
 * device's change. Design: docs/DAY_LOG_STALE_WRITES.md.
 *
 * ⚠️⚠️ A WRITE SENDS A CHANGE, NEVER A COPY. The old writers upserted the whole day
 * from local memory, so a stale tab brought back an entry removed elsewhere, or
 * dropped one added elsewhere. `writeDayRow` reads the row INSIDE the write, applies
 * the one change, and writes only if the row hasn't moved since (compare-and-swap on
 * `updated_at`); if it has, it reads again and re-applies. Local memory is never the
 * base — which also means a tab left open overnight, or a write before the load
 * finished, can't write today from an empty day.
 */

export const CAS_TRIES = 5;

export type DayRowWrite<R> =
  | { ok: true; row: R | null; wrote: boolean }
  | { ok: false; error: unknown };

export interface DayRowTarget {
  table: string;
  userId: string;
  date: string;
  /** The columns to read back (`updated_at` is always added). */
  columns: string;
  /** `log_date` unless the table says otherwise (`menstrual_day_logs.logged_on`). */
  dateColumn?: string;
}

/** Strictly later than the stamp read, so a write always moves it (clocks differ by device). */
export function nextStamp(read: string | null | undefined, now: number = Date.now()): string {
  const t = read ? Date.parse(read) : NaN;
  return new Date(Number.isFinite(t) && t >= now ? t + 1 : now).toISOString();
}

/**
 * Read the day's row, apply `change` to it, write it back if nobody wrote in between.
 * `change` gets the SERVER's row (null = none yet) and returns the columns to write,
 * or null when there is nothing to change (nothing is written then). It may run more
 * than once (once per try), so it must be pure.
 */
export async function writeDayRow<R extends { updated_at: string }>(
  target: DayRowTarget,
  change: (row: R | null) => Record<string, unknown> | null,
): Promise<DayRowWrite<R>> {
  const { table, userId, date } = target;
  const dateColumn = target.dateColumn ?? 'log_date';
  const columns = `${target.columns}, updated_at`;

  for (let i = 0; i < CAS_TRIES; i++) {
    const read = await supabase.from(table).select(columns).eq('user_id', userId).eq(dateColumn, date).maybeSingle();
    if (read.error) return { ok: false, error: read.error };
    const row = (read.data ?? null) as unknown as R | null;
    const patch = change(row);
    if (!patch) return { ok: true, row, wrote: false };

    if (row) {
      const { data, error } = await supabase
        .from(table)
        .update({ ...patch, updated_at: nextStamp(row.updated_at) })
        .eq('user_id', userId)
        .eq(dateColumn, date)
        .eq('updated_at', row.updated_at)
        .select(columns);
      if (error) return { ok: false, error };
      const written = (data as unknown as R[] | null)?.[0];
      if (written) return { ok: true, row: written, wrote: true };
      continue; // it moved under us — read again and re-apply
    }

    const { data, error } = await supabase
      .from(table)
      .insert({ user_id: userId, [dateColumn]: date, ...patch, updated_at: nextStamp(null) })
      .select(columns);
    if (error) {
      // 23505 = another device created the day first; read it and apply on top.
      if ((error as { code?: string }).code === '23505') continue;
      return { ok: false, error };
    }
    const written = (data as unknown as R[] | null)?.[0];
    if (written) return { ok: true, row: written, wrote: true };
    return { ok: false, error: new Error(`${table}: the new row was not returned`) };
  }
  return { ok: false, error: new Error(`${table}: the day kept changing — gave up after ${CAS_TRIES} tries`) };
}

/**
 * One store's day-log work, one job at a time (FIFO). A store's LOAD runs through it
 * too, so a write queued before a load lands first (the load reads it), and a write
 * made during the load waits for it (the load can't overwrite it on screen).
 */
export function serialQueue() {
  let chain: Promise<unknown> = Promise.resolve();
  return <T>(job: () => Promise<T>): Promise<T> => {
    const run = chain.then(job, job);
    chain = run.catch(() => undefined);
    return run;
  };
}

/**
 * This tab's changes still on their way to the server. The screen shows the server's
 * day with these on top, so a quick second tap is decided against the first one's
 * result (a double tap on a pill ticks, then unticks) and a reply to the first write
 * can't wipe the second from the screen.
 */
export function pendingChanges<D>() {
  const ops: { date: string; apply: (d: D) => D }[] = [];
  return {
    add(date: string, apply: (d: D) => D) {
      const op = { date, apply };
      ops.push(op);
      return op;
    },
    settle(op: { date: string; apply: (d: D) => D }) {
      const i = ops.indexOf(op);
      if (i >= 0) ops.splice(i, 1);
    },
    /** `base` (the server's copy of the day) with this tab's unsettled changes applied. */
    over(date: string, base: D): D {
      return ops.reduce((d, o) => (o.date === date ? o.apply(d) : d), base);
    },
    clear() {
      ops.length = 0;
    },
  };
}

/** One id on or off in a day's id list — the same array back when nothing changes. */
export function withId(list: readonly string[], id: string, on: boolean): string[] {
  const has = list.includes(id);
  if (on === has) return list as string[];
  return on ? [...list, id] : list.filter(x => x !== id);
}
